import React from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    Container,
    useTheme,
    alpha,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useState } from 'react';

// Icons
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import GroupIcon from '@mui/icons-material/Group';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import SearchIcon from '@mui/icons-material/Search';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import StorefrontIcon from '@mui/icons-material/Storefront';

import Translation from '../../../utils/Translation';
import { allLinks, checkUserIsCustomer } from '../../../utils/helpers';
import SellerModal from '../../../components/modal/SellerModal';
import Request from './Request';
import SliderHome from './SliderHome';
import TrackShipmentNew from './TrackShipmentNew';
import { Helmet } from 'react-helmet';

// Hero Action Card Component
const ActionCard = ({ icon, title, description, onClick, primary = false, large = false }) => {
    const theme = useTheme();

    return (
        <Card
            onClick={onClick}
            sx={{
                height: '100%',
                cursor: 'pointer',
                borderRadius: 3,
                transition: 'all 0.3s ease',
                border: primary ? `2px solid ${theme.palette.secondary.main}` : '1px solid',
                borderColor: primary ? theme.palette.secondary.main : theme.palette.divider,
                backgroundColor: primary
                    ? alpha(theme.palette.secondary.main, theme.palette.mode === 'dark' ? 0.15 : 0.05)
                    : theme.palette.background.paper,
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.palette.mode === 'dark'
                        ? '0 8px 24px rgba(0,0,0,0.4)'
                        : '0 8px 24px rgba(0,0,0,0.12)',
                    borderColor: theme.palette.secondary.main,
                },
            }}
        >
            <CardContent sx={{ p: large ? 4 : 3 }}>
                <Box
                    sx={{
                        width: large ? 64 : 48,
                        height: large ? 64 : 48,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: primary
                            ? theme.palette.secondary.main
                            : alpha(theme.palette.primary.main, 0.1),
                        color: primary ? '#fff' : theme.palette.primary.main,
                        mb: 2,
                    }}
                >
                    {React.cloneElement(icon, { sx: { fontSize: large ? 32 : 24 } })}
                </Box>

                <Typography
                    variant={large ? 'h5' : 'h6'}
                    fontWeight={600}
                    gutterBottom
                    color="text.primary"
                >
                    {title}
                </Typography>

                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: primary ? 2 : 0 }}
                >
                    {description}
                </Typography>

                {primary && (
                    <Button
                        variant="contained"
                        endIcon={<ArrowForwardIcon />}
                        sx={{
                            mt: 1,
                            backgroundColor: theme.palette.secondary.main,
                            '&:hover': {
                                backgroundColor: theme.palette.primary.main,
                            },
                        }}
                    >
                        <Translation data="home.send-shipment" />
                    </Button>
                )}
            </CardContent>
        </Card>
    );
};

export default function HomeCustomerNew() {
    const navigate = useNavigate();
    const theme = useTheme();
    const { userProfile } = useSelector((state) => state.profile);
    const { userBanner } = useSelector((state) => state.request);
    const [sellerModalOpen, setSellerModalOpen] = useState(false);

    return (
        <>
            <Helmet>
                <title>Deliverit - Delivery Services in UAE</title>
                <meta name="description" content="Deliverit - Fast and reliable delivery services across UAE. Send packages, track shipments, and connect with drivers." />
                <meta name="robots" content="index, follow" />
                <link rel="canonical" href={window.location.origin + '/'} />
            </Helmet>

            {/* Hero Section with Track & Primary Actions */}
            <Box
                sx={{
                    background: theme.palette.mode === 'dark'
                        ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)} 0%, ${alpha(theme.palette.background.default, 1)} 100%)`
                        : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
                    py: { xs: 4, md: 6 },
                    mb: 4,
                    borderRadius: 4
                }}
            >
                <Container maxWidth="lg">
                    <Grid container spacing={4} alignItems="center">
                        {/* Left side - Welcome text and Track */}
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Typography
                                variant="h3"
                                fontWeight={700}
                                color="primary"
                                gutterBottom
                                sx={{ fontSize: { xs: '2rem', md: '2.5rem' } }}
                            >
                                <Translation data="home.head-line-first" />
                            </Typography>
                            <Typography
                                variant="h5"
                                color="text.secondary"
                                sx={{ mb: 3 }}
                            >
                                <Translation data="home.head-line-second" />
                            </Typography>

                            {/* Inline Track Shipment */}
                            <TrackShipmentNew />
                        </Grid>

                        {/* Right side - Primary Action Card */}
                        <Grid size={{ xs: 12, md: 6 }}>
                            <ActionCard
                                icon={<LocalShippingIcon />}
                                title={<Translation data="home.send-shipment" />}
                                description={<Translation data="home.send-shipment-desc" />}
                                onClick={() => navigate(`/${allLinks[3]}`)}
                                primary
                                large
                            />
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* Quick Actions Section */}
            <Container maxWidth="lg" sx={{ mb: 4 }}>
                <Typography
                    variant="h5"
                    fontWeight={600}
                    color="text.primary"
                    sx={{ mb: 3 }}
                >
                    <Translation data="HowCanWeHelp" />
                </Typography>

                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                        <ActionCard
                            icon={<GroupIcon />}
                            title={<Translation data="home.deliverit-sellers" />}
                            description={<Translation data="home.deliverit-sellers-desc" />}
                            onClick={() => setSellerModalOpen(true)}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                        <ActionCard
                            icon={<SupportAgentIcon />}
                            title={<Translation data="home.help-and-support" />}
                            description={<Translation data="home.help-and-support-desc" />}
                            onClick={() => navigate(`/${allLinks[9]}`)}
                        />
                    </Grid>
                </Grid>
            </Container>

            {/* Banner Slider - Only show if banners exist */}
            {userBanner && userBanner.length > 0 && (
                <Container maxWidth="lg" sx={{ mb: 4 }}>
                    <SliderHome />
                </Container>
            )}

            {/* User Requests Section - For logged in customers */}
            {checkUserIsCustomer(userProfile) && (
                <Container maxWidth="lg" sx={{ mb: 4 }}>
                    <Request />
                </Container>
            )}

            {/* Seller Modal */}
            <SellerModal
                open={sellerModalOpen}
                setOpen={setSellerModalOpen}
                handleClickOpen={() => setSellerModalOpen(true)}
                handleClose={() => setSellerModalOpen(false)}
            />
        </>
    );
}
